import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import Layout from '../components/layout';
import Title from '../components/title';
import './bookings.css';
import searchIcon from '../assets/search.svg';
import dropdownIcon from '../assets/dropdown.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const BOOKINGS_PER_PAGE = 3;

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [selectedDate, setSelectedDate] = useState('All Dates');
  const [currentPage, setCurrentPage] = useState(1);
  const [id, setId] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editTickets, setEditTickets] = useState('');
  const [isEditBookingOpen, setIsEditBookingOpen] = useState(false);

  const fetchBookings = async () => {
    const { data } = await supabase
      .from("Bookings")
      .select(`
        *,
        Events(title, title_ar),
        Users(name)
      `);

    setBookings(data || []);
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const editBooking = (pId, pStatus, pTickets) => {
    setId(pId);
    setEditStatus(pStatus);
    setEditTickets(pTickets);
    setIsEditBookingOpen(true);
  };

  const updateBooking = async (e) => {
    e.preventDefault();

    await supabase
      .from("Bookings")
      .update({
        status: editStatus,
        number_of_tickets: editTickets,
      })
      .eq('id', id);

    await fetchBookings();
    setIsEditBookingOpen(false);
  };

  const deleteBooking = async (id) => {
    await supabase.from("Bookings").delete().eq('id', id);
    await fetchBookings();
  };

  const updateBookingStatus = async (id, newStatus) => {
    await supabase.from("Bookings").update({ status: newStatus }).eq('id', id);
    await fetchBookings();
  };

 
  const statusOptions = [...new Set(bookings.map((b) => b.status))];

  const dateOptions = [...new Set(bookings.map((b) => b.created_at.split('T')[0]))];

  const filteredBookings = bookings.filter((booking) => {
    const value = searchTerm.toLowerCase();

    const matchesStatus =
      selectedStatus === 'All Status' ||
      booking.status === selectedStatus;

    const matchesDate =
      selectedDate === 'All Dates' ||
      booking.created_at.split('T')[0] === selectedDate;

    const matchesSearch = (
      booking.id.toString().includes(value) ||
      booking.user_id.toString().includes(value) ||
      booking.Events.title.toLowerCase().includes(value) ||
      booking.status.toLowerCase().includes(value)
    );

    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalPages = Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE);
  const startIndex = (currentPage - 1) * BOOKINGS_PER_PAGE;
  const paginatedBookings = filteredBookings.slice(startIndex, startIndex + BOOKINGS_PER_PAGE);

  return (
    <Layout>
      <div className="bookings_page">
        <Title
          subtitle="Manage all bookings on the Eventra platform"
          title="BOOKINGS MANAGEMENT"
        />

        <div className="bookings_controls_card">
          <div className="bookings_controls_left">
            <label className="bookings_search" htmlFor="bookings-search">
              <img alt="" src={searchIcon} />
              <input
                id="bookings-search"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search bookings..."
                type="text"
                value={searchTerm}
              />
            </label>

            <div className="bookings_filter">
              <select
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
                value={selectedStatus}
              >
                <option value="All Status">All Status</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <img alt="" src={dropdownIcon} />
            </div>

            <div className="bookings_filter">
              <select
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setCurrentPage(1);
                }}
                value={selectedDate}
              >
                <option value="All Dates">All Dates</option>
                {dateOptions.map((date) => (
                  <option key={date} value={date}>
                    {date}
                  </option>
                ))}
              </select>
              <img alt="" src={dropdownIcon} />
            </div>
          </div>
        </div>

        <div className="bookings_table_card">
          <div className="bookings_table_top">
            <div className="bookings_table_title">All Bookings</div>
            <div className="bookings_table_count">{filteredBookings.length} total bookings</div>
          </div>

          <div className="bookings_table_scroll">
            <div className="bookings_row bookings_table_head">
              <div className="bookings_checkbox">
                <input type="checkbox" />
              </div>
              <div>ID</div>
              <div>Created At</div>
              <div>User</div>
              <div>Event</div>
              <div>Tickets</div>
              <div>Total Price</div>
              <div>Status</div>
              <div>Actions</div>
            </div>

            <div className="bookings_table_body">
              {paginatedBookings.map((booking) => (
                <div className="bookings_row" key={booking.id}>
                  <div className="bookings_checkbox">
                    <input type="checkbox" />
                  </div>

                  <div className="bookings_cell_text">{booking.id}</div>
                  <div className="bookings_cell_text">{booking.created_at}</div>
                  <div className="bookings_cell_text">{booking.Users.name}</div>
                  <div className="bookings_cell_text">{booking.Events.title}</div>
                  <div className="bookings_cell_text">{booking.number_of_tickets}</div>
                  <div className="bookings_cell_text">{booking.total_price}</div>
                  <div className="bookings_cell_text">
                    <select
                      className="bookings_status_select"
                      onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                      value={booking.status}
                    >
                      {[...new Set(['confirmed', 'pending', 'cancelled', ...statusOptions])].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="bookings_actions">
                    <button
                      className="bookings_action_button"
                      onClick={() =>
                        editBooking(
                          booking.id,
                          booking.status,
                          booking.number_of_tickets
                        )
                      }
                      type="button"
                    >
                      <img alt="" src={editIcon} />
                    </button>
                    <button className="bookings_action_button" onClick={() => deleteBooking(booking.id)} type="button">
                      <img alt="" src={deleteIcon} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="bookings_pagination">
              <button
                className="bookings_pagination_button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                type="button"
              >
                Previous
              </button>
              <div className="bookings_pagination_info">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="bookings_pagination_button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {isEditBookingOpen && (
          <div
            className="bookings_modal_overlay"
            onClick={() => setIsEditBookingOpen(false)}
            role="presentation"
          >
            <div
              aria-modal="true"
              className="bookings_modal"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
            >
              <div className="bookings_modal_header">
                <div className="bookings_modal_heading">
                  <h2>EDIT BOOKING</h2>
                  <p>Update the booking status and number of tickets.</p>
                </div>

                <button
                  aria-label="Close edit booking overlay"
                  className="bookings_modal_close"
                  onClick={() => setIsEditBookingOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <form
                className="bookings_modal_form"
                onSubmit={updateBooking}
              >
                <div className="bookings_modal_grid">
                  <label className="bookings_modal_field">
                    <span>Status</span>
                    <div className="bookings_modal_select_wrap">
                      <select
                        onChange={(e) => setEditStatus(e.target.value)}
                        value={editStatus}
                      >
                        {statusOptions.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                      <img alt="" src={dropdownIcon} />
                    </div>
                  </label>

                  <label className="bookings_modal_field">
                    <span>Number Of Tickets</span>
                    <input
                      onChange={(e) => setEditTickets(e.target.value)}
                      type="number"
                      value={editTickets}
                    />
                  </label>
                </div>

                <div className="bookings_modal_actions">
                  <button
                    className="bookings_modal_secondary_button"
                    onClick={() => setIsEditBookingOpen(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button className="bookings_modal_primary_button" type="submit">
                    Update Booking
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Bookings;
